import { css } from 'styled-components';

export const SIZES = {
  XX_MICRO: 'xxMicro',
  X_MICRO: 'xMicro',
  MICRO: 'micro',
  XXX_SMALL: 'xxxSmall',
  XX_SMALL: 'xxSmall',
  X_SMALL: 'xSmall',
  SMALL: 'small',
  MEDIUM: 'medium',
  LARGE: 'large',
  X_LARGE: 'xLarge',
  XX_LARGE: 'xxLarge',
  XXX_LARGE: 'xxxLarge',
  MACRO: 'macro',
  NONE: 'none'
} as const;

type Sizes = typeof SIZES;
export type Size = Sizes[keyof Sizes];

export const OFFGRID_SIZES = {
  OFFGRID_1: 'offgrid1',
  OFFGRID_2: 'offgrid2',
  OFFGRID_3: 'offgrid3',
  OFFGRID_6: 'offgrid6',
  OFFGRID_7: 'offgrid7',
  OFFGRID_9: 'offgrid9',
  OFFGRID_10: 'offgrid10',
  OFFGRID_12: 'offgrid12',
  OFFGRID_14: 'offgrid14',
  OFFGRID_18: 'offgrid18',
  OFFGRID_20: 'offgrid20',
  OFFGRID_22: 'offgrid22',
  OFFGRID_26: 'offgrid26'
} as const;

type OffgridSizes = typeof OFFGRID_SIZES;
export type OffgridSize = OffgridSizes[keyof OffgridSizes];

export const PX_TO_REM = {
  0: '0',
  1: '1px',
  2: '0.125rem',
  3: '0.1875rem',
  4: '0.25rem',
  5: '0.3125rem',
  6: '0.375rem',
  7: '0.438rem',
  8: '0.5rem',
  9: '0.563rem',
  10: '0.625rem',
  12: '0.75rem',
  13: '0.813rem',
  14: '0.875rem',
  15: '0.938rem', // Extreme use case for Count
  16: '1rem',
  18: '1.125rem',
  19: '1.188rem', // Extreme use case for Count
  20: '1.25rem',
  22: '1.375rem',
  24: '1.5rem',
  26: '1.625rem',
  28: '1.75rem',
  32: '2rem',
  36: '2.25rem',
  40: '2.5rem',
  44: '2.75rem',
  48: '3rem',
  52: '3.25rem',
  56: '3.5rem',
  60: '3.75rem',
  64: '4rem',
  80: '5rem',
  96: '6rem',
  112: '7rem',
  128: '8rem',
  144: '9rem',
  160: '10rem',
  176: '11rem',
  180: '11.25rem',
  192: '12rem',
  200: '12.5rem',
  208: '13rem',
  224: '14rem',
  240: '15rem',
  256: '16rem',
  320: '20rem',
  336: '21rem',
  352: '22rem',
  400: '25rem',
  416: '26rem',
  528: '33rem',
  576: '36rem',
  600: '37.5rem',
  608: '38rem',
  768: '48rem',
  800: '50rem',
  992: '62rem',
  1024: '64rem',
  1200: '75rem',
  1440: '90rem',
  1920: '120rem'
} as const;

export type PxToRems = typeof PX_TO_REM;
export type PxToRem = PxToRems[keyof PxToRems];

export const sizes: Record<Size | OffgridSize, PxToRem> = {
  [SIZES.NONE]: PX_TO_REM[0],
  [SIZES.XX_MICRO]: PX_TO_REM[8],
  [SIZES.X_MICRO]: PX_TO_REM[12],
  [SIZES.MICRO]: PX_TO_REM[16],
  [SIZES.XXX_SMALL]: PX_TO_REM[18],
  [SIZES.XX_SMALL]: PX_TO_REM[20],
  [SIZES.X_SMALL]: PX_TO_REM[24],
  [SIZES.SMALL]: PX_TO_REM[32], // Core size
  [SIZES.MEDIUM]: PX_TO_REM[40], // Core size
  [SIZES.LARGE]: PX_TO_REM[48], // Core size
  [SIZES.X_LARGE]: PX_TO_REM[64],
  [SIZES.XX_LARGE]: PX_TO_REM[80],
  [SIZES.XXX_LARGE]: PX_TO_REM[96],
  [SIZES.MACRO]: PX_TO_REM[128],
  [OFFGRID_SIZES.OFFGRID_1]: PX_TO_REM[1],
  [OFFGRID_SIZES.OFFGRID_2]: PX_TO_REM[2],
  [OFFGRID_SIZES.OFFGRID_3]: PX_TO_REM[3],
  [OFFGRID_SIZES.OFFGRID_6]: PX_TO_REM[6],
  [OFFGRID_SIZES.OFFGRID_7]: PX_TO_REM[7],
  [OFFGRID_SIZES.OFFGRID_9]: PX_TO_REM[9],
  [OFFGRID_SIZES.OFFGRID_10]: PX_TO_REM[10],
  [OFFGRID_SIZES.OFFGRID_12]: PX_TO_REM[12],
  [OFFGRID_SIZES.OFFGRID_14]: PX_TO_REM[14],
  [OFFGRID_SIZES.OFFGRID_18]: PX_TO_REM[18],
  [OFFGRID_SIZES.OFFGRID_20]: PX_TO_REM[20],
  [OFFGRID_SIZES.OFFGRID_22]: PX_TO_REM[22],
  [OFFGRID_SIZES.OFFGRID_26]: PX_TO_REM[26]
};

export const TABLE_HEIGHT_HEADER = 48;
export const TABLE_HEIGHT_FOOTER = 64;
export const TABLE_HEIGHT_PER_ROW = 64;
export const TABLE_HEIGHT_DEFAULT_ROWS = 8.25;

export const useModuleMaxHeightRowsCss = (rows: number = TABLE_HEIGHT_DEFAULT_ROWS) => css`
  max-height: calc(${PX_TO_REM[TABLE_HEIGHT_HEADER]} + ${PX_TO_REM[TABLE_HEIGHT_FOOTER]} + ${rows} * ${PX_TO_REM[TABLE_HEIGHT_PER_ROW]});
`;

export const useModuleMaxHeightCss = useModuleMaxHeightRowsCss();
